import { get, map, forEach, isEmpty, unionBy, find } from "lodash";
import { Layout, Row, Col, Table, Button, Result } from "antd";
import { FrownOutlined, DeleteOutlined } from "@ant-design/icons";

import dynamic from "next/dynamic";
const AddToCartButton = dynamic(() =>
  import("components/cart/AddToCartButton")
);
const ProductCard = dynamic(() =>
  import("components/product/card/ProductCard")
);

import useSWR from "swr";
import useCompare from "grandus-lib/hooks/useCompare";

import styles from "pages/porovnanie/compare.page.module.scss";

const Compare = () => {
  const { compare, isLoading, itemRemove } = useCompare();

  const productsUrl = !isEmpty(get(compare, "productIds", []))
    ? `productIds=${get(compare, "productIds", []).join("&productIds=")}`
    : false;

  const { data: products, isValidating } = useSWR(
    productsUrl
      ? `/api/lib/v1/products?${productsUrl}&fields=parameters,store,ean&perPage=${
          get(compare, "productIds", []).length
        }`
      : null,
    (url) => fetch(url).then((r) => r.json()),
    {
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  const productsArray = get(products, "products", []);

  if (isEmpty(productsArray) && !isLoading && !isValidating) {
    return (
      <Layout className="container guttered">
        <Layout.Content>
          <Result
            icon={<FrownOutlined />}
            title="Porovnanie je prázdne"
            subTitle={`Po vložení produktov do zoznamu porovnania, sa zobrazí prehladná tabuľka obsahujúca všetky parametre produktov, tak, aby ste jednoducho mohli nájsť rozdiely.`}
          />
        </Layout.Content>
      </Layout>
    );
  }

  let columns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
      fixed: "left",
    },
  ];

  let data = [];
  let dataInitial = {};
  let dataFetchedProductIds = [];
  let dataFetchedProductEans = [];
  let dataFetchedProductAddToCarts = [];
  let dataFetchedProductActions = [];

  map(productsArray, (product) => {
    dataFetchedProductIds[`product${get(product, "id")}`] = get(product, "id");
    dataFetchedProductEans[`product${get(product, "id")}`] = get(
      product,
      "ean",
      "---"
    );
    dataFetchedProductAddToCarts[`product${get(product, "id")}`] = (
      <AddToCartButton
        stores={get(product, "store", [])}
        productId={get(product, "id")}
      />
    );
    dataFetchedProductActions[`product${get(product, "id")}`] = isLoading ? (
      "---"
    ) : (
      <Button
        type="text"
        danger
        onClick={(e) => itemRemove(get(product, "id"))}
      >
        <DeleteOutlined /> Odstrániť
      </Button>
    );

    columns.push({
      title: get(product, "name"),
      dataIndex: `product${get(product, "id")}`,
      key: `product${get(product, "id")}`,
    });

    dataInitial[`product${get(product, "id")}`] = (
      <div className={styles?.productCard} style={{ width: productsArray.length > 3 ? 270 : '100%' }}>
        <ProductCard {...product} />
      </div>
    );
  });

  data.push({
    key: "initial01",
    name: "",
    ...dataInitial,
  });

  data.push({
    key: "initial02",
    name: "",
    ...dataFetchedProductActions,
  });

  data.push({
    key: "initial03",
    name: "",
    ...dataFetchedProductAddToCarts,
  });

  data.push({
    key: "initial04",
    name: <strong>ID</strong>,
    ...dataFetchedProductIds,
  });

  data.push({
    key: "initial05",
    name: <strong>EAN</strong>,
    ...dataFetchedProductEans,
  });

  let allParams = [];
  forEach(productsArray, (product) => {
    const params = get(product, "parameters");
    if (!isEmpty(params)) {
      const paramsIds = map(params, (param) => {
        return {
          parameterId: get(param, "parameterId"),
          name: get(param, "name"),
        };
      });
      allParams = [].concat(unionBy(allParams, paramsIds, "parameterId"));
    }
  });

  map(allParams, (param) => {
    const { name, parameterId } = param;

    let row = {};

    map(productsArray, (product, index) => {
      row[`product${get(product, "id")}`] = get(
        find(
          get(productsArray, `[${index}].parameters`),
          (param) => param.parameterId === parameterId
        ),
        "value",
        "---"
      );
    });

    data.push({
      key: parameterId,
      name: <strong>{name}</strong>,
      ...row,
    });
  });

  if (data.length >= 15) {
    data.push({
      key: "ending01",
      name: "",
      ...dataInitial,
    });
  }

  return (
    <Layout className="container guttered">
      <Layout.Content>
        <Row>
          <Col span={24}>
            <Table
              loading={isLoading || isValidating}
              bordered={true}
              pagination={false}
              columns={columns}
              dataSource={data}
              scroll={{ x: true }}
            />
          </Col>
        </Row>
      </Layout.Content>
    </Layout>
  );
};

export default Compare;
